import type { Product, Order, StockMovement, Category, User, Promotion, Announcement, Expense, Agency, CurrentAdminUser, Role, AgencyMembership, Permission } from "./types"
import { apiService } from "./api-service"
import { API_ENDPOINTS, API_BASE_URL, getAuthHeaders } from "./api-config"
import { normalizePayment, normalizePayments } from "./normalize"
import { toast } from "sonner";

const expenseTypeToApi = {
    ordinary: "ORDINARY",
    stock: "STOCK",
} as const;

const expenseStatusToApi = {
    paid: "PAID",
    pending: "PENDING",
} as const;

const expensePaymentMethodToApi = {
    cash: "CASH",
    mobile_money: "MOBILE_MONEY",
    bank_transfer: "BANK_TRANSFER",
    credit_card: "CREDIT_CARD",
    check: "CHECK",
    other: "OTHER",
} as const;

function toApiEnum<T extends Record<string, string>>(value: unknown, map: T) {
    if (typeof value !== "string" || !value) return value;
    return map[value as keyof T] || value.toUpperCase();
}

function hasValidDateRange(dateFrom?: string, dateTo?: string) {
    if (!dateFrom || !dateTo) return true;
    return new Date(dateFrom).getTime() <= new Date(dateTo).getTime();
}

function normalizeExpensePayload<T extends Record<string, any>>(payload: T) {
    const normalized: Record<string, any> = { ...payload };

    if (normalized.type) {
        normalized.type = toApiEnum(normalized.type, expenseTypeToApi);
    }

    if (normalized.status) {
        normalized.status = toApiEnum(normalized.status, expenseStatusToApi);
    }

    if (normalized.paymentMethod) {
        normalized.paymentMethod = toApiEnum(normalized.paymentMethod, expensePaymentMethodToApi);
    }

    if (normalized.date && !normalized.expenseDate) {
        normalized.expenseDate = normalized.date;
        delete normalized.date;
    }

    Object.keys(normalized).forEach((key) => {
        if (normalized[key] === undefined) delete normalized[key];
    });

    return normalized;
}

function agencyHeaders(agencyId?: string | null): Record<string, string> | undefined {
    if (!agencyId) return undefined;
    const { Authorization } = getAuthHeaders();
    return { ...(Authorization ? { Authorization } : {}), 'X-Agency-Id': agencyId };
}

function normalizeProduct(p: any): any {
    if (!p) return p;
    const cat = p.category;
    return {
        ...p,
        categoryId: p.categoryId ?? (cat && typeof cat === 'object' ? cat.id : null),
        category: cat && typeof cat === 'object' ? (cat.name ?? '') : (cat ?? ''),
        categoryPromotions: cat && typeof cat === 'object' ? (cat.promotions ?? []) : [],
    };
}

export const apiStore = {
    // Products
    async getProducts(params?: { category?: string; search?: string; sortBy?: string; page?: number; limit?: number; includeInactive?: boolean }): Promise<Product[]> {
        const queryParams: any = { ...params };
        if (params?.includeInactive) {
            queryParams.includeInactive = 'true';
        }
        const response: any = await apiService.get(API_ENDPOINTS.PRODUCTS, queryParams);
        return (response.data || response || []).map(normalizeProduct);
    },

    async getProductsPaginated(params?: { category?: string; search?: string; sortBy?: string; page?: number; limit?: number; includeInactive?: boolean }): Promise<{
        products: Product[];
        pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
        };
    }> {
        const queryParams: any = { ...params };
        if (params?.includeInactive) {
            queryParams.includeInactive = 'true';
        }

        const response: any = await apiService.get(API_ENDPOINTS.PRODUCTS, queryParams);

        return {
            products: (response.data || []).map(normalizeProduct),
            pagination: response.pagination || {
                page: params?.page || 1,
                limit: params?.limit || 100,
                total: 0,
                totalPages: 0,
            },
        };
    },

    async getProductById(id: string): Promise<Product> {
        const response: any = await apiService.get(API_ENDPOINTS.PRODUCT_BY_ID(id));
        return normalizeProduct(response.data || response);
    },

    async getProductsSections(): Promise<{
        suggestions: Product[];
        bestSellers: Product[];
        mostLiked: Product[];
        mostViewed: Product[];
        newArrivals: Product[];
    }> {
        const response: any = await apiService.get(API_ENDPOINTS.PRODUCT_SECTIONS);
        const sections = response.data || response;
        const normalizeSection = (arr: any[]) => (arr || []).map(normalizeProduct);
        return {
            suggestions: normalizeSection(sections.suggestions),
            bestSellers: normalizeSection(sections.bestSellers),
            mostLiked: normalizeSection(sections.mostLiked),
            mostViewed: normalizeSection(sections.mostViewed),
            newArrivals: normalizeSection(sections.newArrivals),
        };
    },

    async getProductsBySection(type: string, page: number = 1, limit: number = 12): Promise<{
        products: Product[];
        pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
            hasMore: boolean;
        };
    }> {
        const response: any = await apiService.get(`/products/sections/${type}`, { page, limit });
        return {
            products: (response.data || []).map(normalizeProduct),
            pagination: response.pagination || {
                page: 1,
                limit: 12,
                total: 0,
                totalPages: 0,
                hasMore: false
            }
        };
    },

    async searchProducts(query: string, page: number = 1, limit: number = 12): Promise<{
        products: Product[];
        pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
            hasMore: boolean;
        };
        query: string;
    }> {
        const response: any = await apiService.get('/products/search', { q: query, page, limit });
        return {
            products: (response.data || []).map(normalizeProduct),
            pagination: response.pagination || {
                page: 1,
                limit: 12,
                total: 0,
                totalPages: 0,
                hasMore: false
            },
            query: response.query || query
        };
    },

    async getRandomProducts(page: number = 1, limit: number = 12): Promise<{
        products: Product[];
        pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
            hasMore: boolean;
        };
    }> {
        const response: any = await apiService.get('/products/random', { page, limit });
        return {
            products: (response.data || []).map(normalizeProduct),
            pagination: response.pagination || {
                page: 1,
                limit: 12,
                total: 0,
                totalPages: 0,
                hasMore: false
            }
        };
    },

    async addProduct(product: Omit<Product, "id" | "createdAt" | "views" | "likes" | "sales">): Promise<Product> {
        const response: any = await apiService.post(API_ENDPOINTS.PRODUCTS, product);
        return normalizeProduct(response.data || response);
    },

    async addProductForAgency(agencyId: string, product: Omit<Product, "id" | "createdAt" | "views" | "likes" | "sales">): Promise<Product> {
        const { Authorization } = getAuthHeaders();
        const response: any = await apiService.post(API_ENDPOINTS.PRODUCTS, product, {
            ...(Authorization ? { Authorization } : {}),
            'X-Agency-Id': agencyId,
        });
        return normalizeProduct(response.data || response);
    },

    async updateProduct(id: string, updates: Partial<Product>, agencyId?: string | null): Promise<Product> {
        const response: any = await apiService.put(API_ENDPOINTS.PRODUCT_BY_ID(id), updates, agencyHeaders(agencyId));
        return normalizeProduct(response.data || response);
    },

    async deleteProduct(id: string, agencyId?: string | null): Promise<void> {
        await apiService.delete(API_ENDPOINTS.PRODUCT_BY_ID(id), agencyHeaders(agencyId));
    },

    async toggleLike(productId: string, action: 'like' | 'unlike'): Promise<Product> {
        const response: any = await apiService.post(API_ENDPOINTS.PRODUCT_LIKE(productId), { action });
        return normalizeProduct(response.data || response);
    },

    async toggleProductStatus(id: string, isActive: boolean, agencyId?: string | null): Promise<Product> {
        const response: any = await apiService.put(API_ENDPOINTS.PRODUCT_BY_ID(id), { isActive }, agencyHeaders(agencyId));
        return normalizeProduct(response.data || response);
    },

    async getProductStats(): Promise<any> {
        const response: any = await apiService.get(API_ENDPOINTS.PRODUCT_STATS);
        return response.data || response;
    },

    // Orders
    async getOrders(params?: { status?: string; search?: string; dateFrom?: string; dateTo?: string; page?: number; limit?: number; period?: string }): Promise<any> {
        const response: any = await apiService.get(API_ENDPOINTS.ORDERS, params as any);
        return response;
    },

    async getOrderById(id: string): Promise<Order> {
        const response: any = await apiService.get(API_ENDPOINTS.ORDER_BY_ID(id));
        return response.data || response;
    },

    async addOrder(order: Omit<Order, "id" | "createdAt" | "status">): Promise<Order> {
        const response: any = await apiService.post(API_ENDPOINTS.ORDERS, order);
        return response.data || response;
    },

    async updateOrderStatus(id: string, status: "pending" | "processing" | "completed" | "cancelled"): Promise<Order> {
        const response: any = await apiService.patch(API_ENDPOINTS.ORDER_STATUS(id), { status });
        return response.data || response;
    },

    async validateOrder(id: string, paymentType?: string): Promise<{ data: Order; movement: any; message: string }> {
        const response: any = await apiService.post(API_ENDPOINTS.ORDER_VALIDATE(id), { paymentType });
        return response;
    },

    async deleteOrder(id: string): Promise<void> {
        await apiService.delete(API_ENDPOINTS.ORDER_BY_ID(id));
    },

    async getOrderStats(params?: { dateFrom?: string; dateTo?: string }): Promise<any> {
        const response: any = await apiService.get(API_ENDPOINTS.ORDER_STATS, params as any);
        return response.data || response;
    },

    // Stock Movements
    async getMovements(params?: { type?: string; productId?: string; dateFrom?: string; dateTo?: string; page?: number; limit?: number; search?: string; period?: string }): Promise<any> {
        const response: any = await apiService.get(API_ENDPOINTS.STOCK_MOVEMENTS, params as any);
        return response;
    },

    async getMovementById(id: string): Promise<StockMovement> {
        const response: any = await apiService.get(API_ENDPOINTS.STOCK_MOVEMENT_BY_ID(id));
        return response.data || response;
    },

    async addMovement(movement: Omit<StockMovement, "id" | "date">): Promise<StockMovement> {
        const response: any = await apiService.post(API_ENDPOINTS.STOCK_MOVEMENTS, movement);
        return response.data || response;
    },

    async deleteMovement(id: string): Promise<void> {
        await apiService.delete(API_ENDPOINTS.STOCK_MOVEMENT_BY_ID(id));
    },

    async getMovementStats(params?: { dateFrom?: string; dateTo?: string }): Promise<any> {
        const response: any = await apiService.get(API_ENDPOINTS.STOCK_MOVEMENT_STATS, params as any);
        return response.data || response;
    },

    // Categories
    async getCategories(search?: string): Promise<Category[]> {
        const response: any = await apiService.get(API_ENDPOINTS.CATEGORIES, search ? { search } : undefined);
        return response.data || response || [];
    },

    async getAllCategories(search?: string): Promise<Category[]> {
        const response: any = await apiService.get(API_ENDPOINTS.CATEGORIES_ALL, search ? { search } : undefined);
        return response.data || response || [];
    },

    async getCategoryById(id: string): Promise<Category> {
        const response: any = await apiService.get(API_ENDPOINTS.CATEGORY_BY_ID(id));
        return response.data || response;
    },

    async addCategory(category: Omit<Category, "id">): Promise<Category> {
        const response: any = await apiService.post(API_ENDPOINTS.CATEGORIES, category);
        return response.data || response;
    },

    async addCategoryForAgency(agencyId: string, category: Omit<Category, "id">): Promise<Category> {
        const { Authorization } = getAuthHeaders();
        const response: any = await apiService.post(API_ENDPOINTS.CATEGORIES, category, {
            ...(Authorization ? { Authorization } : {}),
            'X-Agency-Id': agencyId,
        });
        return response.data || response;
    },

    async updateCategory(id: string, updates: Partial<Category>, agencyId?: string | null): Promise<Category> {
        const response: any = await apiService.put(API_ENDPOINTS.CATEGORY_BY_ID(id), updates, agencyHeaders(agencyId));
        return response.data || response;
    },

    async deleteCategory(id: string, agencyId?: string | null): Promise<void> {
        await apiService.delete(API_ENDPOINTS.CATEGORY_BY_ID(id), agencyHeaders(agencyId));
    },

    async getCategoryStats(id: string): Promise<any> {
        const response: any = await apiService.get(API_ENDPOINTS.CATEGORY_STATS(id));
        return response.data || response;
    },

    // Users
    async getUsers(params?: { page?: number; limit?: number; search?: string; includeInactive?: boolean }): Promise<User[]> {
        const response: any = await apiService.get(API_ENDPOINTS.USERS, params as any);
        return response.data || response || [];
    },

    async getUserById(id: string): Promise<User> {
        const response: any = await apiService.get(API_ENDPOINTS.USER_BY_ID(id));
        return response.data || response;
    },

    async getUserByUsername(username: string): Promise<User> {
        const response: any = await apiService.get(API_ENDPOINTS.USER_BY_USERNAME(username));
        return response.data || response;
    },

    async addUser(user: { username: string; fullName?: string; nom_prenom?: string; password: string; email?: string; phone?: string; isSuperAdmin?: boolean; isActive?: boolean }): Promise<User> {
        const response: any = await apiService.post(API_ENDPOINTS.REGISTER, user);
        return response.data || response;
    },

    async updateUser(id: string, user: { fullName?: string; nom_prenom?: string; password?: string; email?: string; phone?: string; isSuperAdmin?: boolean; isActive?: boolean }): Promise<User> {
        const response: any = await apiService.put(API_ENDPOINTS.USER_BY_ID(id), user);
        return response.data || response;
    },

    async deleteUser(id: string): Promise<void> {
        await apiService.delete(API_ENDPOINTS.USER_BY_ID(id));
    },

    async getRoles(): Promise<Role[]> {
        const response: any = await apiService.get(API_ENDPOINTS.ROLES);
        return response.data || response || [];
    },

    async createRole(payload: { key: string; name: string; description?: string; agencyId?: string | null }): Promise<Role> {
        const response: any = await apiService.post(API_ENDPOINTS.ROLES, payload);
        return response.data || response;
    },

    async updateRole(id: string, payload: { key?: string; name?: string; description?: string }): Promise<Role> {
        const response: any = await apiService.put(API_ENDPOINTS.ROLE_BY_ID(id), payload);
        return response.data || response;
    },

    async deleteRole(id: string): Promise<void> {
        await apiService.delete(API_ENDPOINTS.ROLE_BY_ID(id));
    },

    async getPermissions(): Promise<Permission[]> {
        const response: any = await apiService.get(API_ENDPOINTS.PERMISSIONS);
        return response.data || response || [];
    },

    async replaceRolePermissions(id: string, permissionIds: string[]): Promise<Role> {
        const response: any = await apiService.put(`${API_ENDPOINTS.ROLE_BY_ID(id)}/permissions`, { permissionIds });
        return response.data || response;
    },

    async getUserMemberships(userId: string): Promise<AgencyMembership[]> {
        const response: any = await apiService.get(API_ENDPOINTS.USER_MEMBERSHIPS(userId));
        return response.data || response || [];
    },

    async assignUserMembership(userId: string, payload: { agencyId: string; roleId: string }): Promise<AgencyMembership> {
        const response: any = await apiService.post(API_ENDPOINTS.USER_MEMBERSHIPS(userId), payload);
        return response.data || response;
    },

    async updateUserMembership(userId: string, membershipId: string, payload: { roleId?: string; isActive?: boolean }): Promise<AgencyMembership> {
        const response: any = await apiService.put(API_ENDPOINTS.USER_MEMBERSHIP_BY_ID(userId, membershipId), payload);
        return response.data || response;
    },

    async deleteUserMembership(userId: string, membershipId: string): Promise<void> {
        await apiService.delete(API_ENDPOINTS.USER_MEMBERSHIP_BY_ID(userId, membershipId));
    },

    // Promotions
    async getPromotions(): Promise<Promotion[]> {
        const response: any = await apiService.get(API_ENDPOINTS.PROMOTIONS);
        return response.data || response.promotions || response || [];
    },

    async getAllPromotions(): Promise<Promotion[]> {
        const response: any = await apiService.get(API_ENDPOINTS.PROMOTIONS_ADMIN);
        return response.data || response.promotions || response || [];
    },

    async getPromotionById(id: string): Promise<Promotion> {
        const response: any = await apiService.get(API_ENDPOINTS.PROMOTION_BY_ID(id));
        return response.data || response.promotion || response;
    },

    async addPromotion(promotion: {
        name: string;
        description?: string;
        discountType: "PERCENTAGE" | "FIXED";
        discountValue: number;
        applicationType: "GENERAL" | "CATEGORY" | "PRODUCT";
        productId?: string | null;
        categoryId?: string | null;
        startDate: string;
        endDate: string;
        isActive?: boolean;
        maxUsageCount?: number | null;
        priority?: number;
    }): Promise<Promotion> {
        const response: any = await apiService.post(API_ENDPOINTS.PROMOTIONS, promotion);
        return response.data || response;
    },

    async updatePromotion(id: string, updates: Partial<Promotion>): Promise<Promotion> {
        const response: any = await apiService.put(API_ENDPOINTS.PROMOTION_BY_ID(id), updates);
        return response.data || response;
    },

    async deletePromotion(id: string): Promise<void> {
        await apiService.delete(API_ENDPOINTS.PROMOTION_BY_ID(id));
    },

    // Announcements
    async getPublishedAnnouncements(params?: { limit?: number }): Promise<Announcement[]> {
        const response: any = await apiService.get(API_ENDPOINTS.ANNOUNCEMENTS, params as any);
        return response.data || response || [];
    },

    async getAllAnnouncements(params?: { page?: number; limit?: number; published?: boolean; search?: string }): Promise<any> {
        const response: any = await apiService.get(API_ENDPOINTS.ANNOUNCEMENTS_ADMIN, params as any);
        return response.data || response || [];
    },

    async addAnnouncement(announcement: { title: string; description: string; image?: string; isPublished?: boolean }): Promise<Announcement> {
        const response: any = await apiService.post(API_ENDPOINTS.ANNOUNCEMENTS, announcement);
        return response.data || response;
    },

    async updateAnnouncement(id: string, updates: Partial<Announcement>): Promise<Announcement> {
        const response: any = await apiService.put(API_ENDPOINTS.ANNOUNCEMENT_BY_ID(id), updates);
        return response.data || response;
    },

    async deleteAnnouncement(id: string): Promise<void> {
        await apiService.delete(API_ENDPOINTS.ANNOUNCEMENT_BY_ID(id));
    },

    // Expenses
    async getExpenses(params?: {
        page?: number;
        limit?: number;
        type?: 'ordinary' | 'stock';
        status?: 'paid' | 'pending';
        search?: string;
        dateFrom?: string;
        dateTo?: string;
    }): Promise<any> {
        const queryParams: Record<string, any> = {
            ...(params || {}),
            type: params?.type ? toApiEnum(params.type, expenseTypeToApi) : undefined,
            status: params?.status ? toApiEnum(params.status, expenseStatusToApi) : undefined,
        };

        if (!hasValidDateRange(params?.dateFrom, params?.dateTo)) {
            delete queryParams.dateFrom;
            delete queryParams.dateTo;
        }

        Object.keys(queryParams).forEach((key) => {
            if (queryParams[key] === undefined || queryParams[key] === "") delete queryParams[key];
        });

        const response: any = await apiService.get(API_ENDPOINTS.EXPENSES, queryParams);
        return response;
    },

    async getExpenseById(id: string): Promise<Expense> {
        const response: any = await apiService.get(API_ENDPOINTS.EXPENSE_BY_ID(id));
        return response.data || response;
    },

    async addExpense(expense: {
        title: string;
        description?: string;
        amount: number;
        paymentMethod: string;
        date?: string;
        files?: string[];
        status?: 'paid' | 'pending';
    }): Promise<Expense> {
        const response: any = await apiService.post(API_ENDPOINTS.EXPENSES, normalizeExpensePayload(expense));
        return response.data || response;
    },

    async updateExpense(id: string, updates: Partial<Expense>): Promise<Expense> {
        const response: any = await apiService.put(API_ENDPOINTS.EXPENSE_BY_ID(id), normalizeExpensePayload(updates as any));
        return response.data || response;
    },

    async deleteExpense(id: string): Promise<void> {
        await apiService.delete(API_ENDPOINTS.EXPENSE_BY_ID(id));
    },

    async addExpenseInstallment(expenseId: string, data: {
        amount: number;
        paymentMethod: string;
        paymentDate?: string;
        notes?: string;
    }): Promise<Expense> {
        const response: any = await apiService.post(API_ENDPOINTS.EXPENSE_INSTALLMENTS(expenseId), data);
        return response.data || response;
    },

    async deleteExpenseInstallment(expenseId: string, installmentId: string): Promise<Expense> {
        const response: any = await apiService.delete(API_ENDPOINTS.EXPENSE_INSTALLMENT_BY_ID(expenseId, installmentId));
        return response.data || response;
    },

    // Stats
    async getDashboardSummary(params?: { period?: 'today' | 'week' | 'month' | 'all'; startDate?: string; endDate?: string; scope?: 'agency' | 'global' }): Promise<any> {
        const queryParams = new URLSearchParams();
        if (params?.period) queryParams.append('period', params.period);
        if (params?.startDate) queryParams.append('startDate', params.startDate);
        if (params?.endDate) queryParams.append('endDate', params.endDate);
        if (params?.scope) queryParams.append('scope', params.scope);

        const url = `${API_ENDPOINTS.STATS_DASHBOARD_SUMMARY}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        const response: any = await apiService.get(url);
        return response.data || response;
    },

    async getProfitStats(params?: { period?: 'today' | 'week' | 'month' | 'all'; startDate?: string; endDate?: string; scope?: 'agency' | 'global' }): Promise<{
        totalRevenue: number;
        totalCost: number;
        profit: number;
        profitMargin: number;
        totalItemsSold: number;
        totalOrders: number;
        period: string;
        dateRange: { startDate: string; endDate: string } | null;
    }> {
        const queryParams = new URLSearchParams();
        if (params?.period) queryParams.append('period', params.period);
        if (params?.startDate) queryParams.append('startDate', params.startDate);
        if (params?.endDate) queryParams.append('endDate', params.endDate);
        if (params?.scope) queryParams.append('scope', params.scope);

        const url = `${API_ENDPOINTS.STATS_PROFIT}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        const response: any = await apiService.get(url);
        return response.data || response;
    },

    async getExpenseStats(params?: { period?: 'today' | 'week' | 'month' | 'all'; startDate?: string; endDate?: string; scope?: 'agency' | 'global' }): Promise<{
        totalCount: number;
        totalAmount: number;
        paidAmount: number;
        pendingAmount: number;
        ordinaryAmount: number;
        stockAmount: number;
        period: string;
        dateRange: { startDate: string; endDate: string } | null;
    }> {
        const queryParams = new URLSearchParams();
        if (params?.period) queryParams.append('period', params.period);
        if (params?.startDate) queryParams.append('startDate', params.startDate);
        if (params?.endDate) queryParams.append('endDate', params.endDate);
        if (params?.scope) queryParams.append('scope', params.scope);

        const url = `${API_ENDPOINTS.STATS_EXPENSES}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        const response: any = await apiService.get(url);
        return response.data || response;
    },

    async getExpensesEvolution(params?: { period?: 'today' | 'week' | 'month' | 'all'; startDate?: string; endDate?: string; scope?: 'agency' | 'global' }): Promise<Array<{
        date: string;
        expenses: number;
        ordinary: number;
        stock: number;
        count: number;
    }>> {
        const queryParams = new URLSearchParams();
        if (params?.period) queryParams.append('period', params.period);
        if (params?.startDate) queryParams.append('startDate', params.startDate);
        if (params?.endDate) queryParams.append('endDate', params.endDate);
        if (params?.scope) queryParams.append('scope', params.scope);

        const url = `${API_ENDPOINTS.STATS_EXPENSES_EVOLUTION}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        const response: any = await apiService.get(url);
        const data = response.data || response || [];
        return Array.isArray(data) ? data : [];
    },

    async getSalesEvolution(params?: { period?: 'today' | 'week' | 'month' | 'all'; startDate?: string; endDate?: string; scope?: 'agency' | 'global' }): Promise<Array<{
        date: string;
        sales: number;
        quantity: number;
        orders: number;
    }>> {
        const queryParams = new URLSearchParams();
        if (params?.period) queryParams.append('period', params.period);
        if (params?.startDate) queryParams.append('startDate', params.startDate);
        if (params?.endDate) queryParams.append('endDate', params.endDate);
        if (params?.scope) queryParams.append('scope', params.scope);

        const url = `${API_ENDPOINTS.STATS_SALES_EVOLUTION}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        const response: any = await apiService.get(url);
        // L'API peut retourner soit {data: [...]} soit directement le tableau
        const data = response.data || response || [];
        return Array.isArray(data) ? data : [];
    },

    // Auth
    async login(credentials: { username: string; password: string }): Promise<{ access_token: string; refresh_token: string; token_type: string; expires_in: number; user: CurrentAdminUser }> {
        const response: any = await apiService.post(API_ENDPOINTS.LOGIN, credentials);
        return response.data || response;
    },

    async getMe(): Promise<CurrentAdminUser> {
        const response: any = await apiService.get(API_ENDPOINTS.ME);
        return response.data || response;
    },

    async getMyAgencies(): Promise<Agency[]> {
        const response: any = await apiService.get(API_ENDPOINTS.MY_AGENCIES);
        return response.data || response || [];
    },

    async getMyPermissions(): Promise<string[]> {
        const response: any = await apiService.get(API_ENDPOINTS.MY_PERMISSIONS);
        return response.data || response || [];
    },

    async getAgencies(params?: { includeInactive?: boolean }): Promise<Agency[]> {
        const response: any = await apiService.get(API_ENDPOINTS.AGENCIES, params as any);
        return response.data || response || [];
    },

    async createAgency(payload: { name: string; address?: string; phone?: string; email?: string; isActive?: boolean }): Promise<Agency> {
        const response: any = await apiService.post(API_ENDPOINTS.AGENCIES, payload);
        return response.data || response;
    },

    async updateAgency(id: string, payload: { name?: string; address?: string; phone?: string; email?: string; isActive?: boolean }): Promise<Agency> {
        const response: any = await apiService.put(API_ENDPOINTS.AGENCY_BY_ID(id), payload);
        return response.data || response;
    },

    async deleteAgency(id: string): Promise<void> {
        await apiService.delete(API_ENDPOINTS.AGENCY_BY_ID(id));
    },

    // Upload
    async uploadImages(files: File[]): Promise<{ images: string[]; count: number }> {
        const formData = new FormData();
        files.forEach(file => {
            formData.append('images', file);
        });

        const authHeaders = getAuthHeaders();
        const headers: HeadersInit = {
            ...(authHeaders.Authorization ? { 'Authorization': authHeaders.Authorization } : {}),
            ...(authHeaders['X-Agency-Id'] ? { 'X-Agency-Id': authHeaders['X-Agency-Id'] } : {}),
        };

        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.UPLOAD_IMAGES}`, {
            method: 'POST',
            headers,
            body: formData,
        });

        const result = await response.json();

        if (!response.ok) {
            // afficher une toast d'erreur
            toast.error(result.message || 'Erreur lors de l\'upload des images');
            throw new Error(result.message || 'Erreur lors de l\'upload des images');
        }

        return result.data;
    },

    async uploadFiles(files: File[]): Promise<{ files: string[]; count: number }> {
        const formData = new FormData();
        files.forEach(file => {
            formData.append('files', file);
        });

        const authHeaders = getAuthHeaders();
        const headers: HeadersInit = {
            ...(authHeaders.Authorization ? { 'Authorization': authHeaders.Authorization } : {}),
            ...(authHeaders['X-Agency-Id'] ? { 'X-Agency-Id': authHeaders['X-Agency-Id'] } : {}),
        };

        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.UPLOAD_FILES}`, {
            method: 'POST',
            headers,
            body: formData,
        });

        const result = await response.json();

        if (!response.ok) {
            toast.error(result.message || 'Erreur lors de l\'upload des fichiers');
            throw new Error(result.message || 'Erreur lors de l\'upload des fichiers');
        }

        return result.data;
    },

    async uploadImage(file: File): Promise<{ image: string }> {
        const formData = new FormData();
        formData.append('image', file);

        const authHeaders = getAuthHeaders();
        const headers: HeadersInit = {
            ...(authHeaders.Authorization ? { 'Authorization': authHeaders.Authorization } : {}),
            ...(authHeaders['X-Agency-Id'] ? { 'X-Agency-Id': authHeaders['X-Agency-Id'] } : {}),
        };

        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.UPLOAD_IMAGE}`, {
            method: 'POST',
            headers,
            body: formData,
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Erreur lors de l\'upload de l\'image');
        }

        const result = await response.json();
        return result.data;
    },

    async uploadFile(file: File): Promise<{ file: string }> {
        const formData = new FormData();
        formData.append('file', file);

        const authHeaders = getAuthHeaders();
        const headers: HeadersInit = authHeaders.Authorization
            ? { 'Authorization': authHeaders.Authorization }
            : {};

        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.UPLOAD_FILE}`, {
            method: 'POST',
            headers,
            body: formData,
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Erreur lors de l\'upload du fichier');
        }

        const result = await response.json();
        return result.data;
    },

    async deleteImage(pathOrFilename: string): Promise<void> {
        // Encoder le chemin pour gérer les slashes et caractères spéciaux
        const encoded = encodeURIComponent(pathOrFilename);
        await apiService.delete(API_ENDPOINTS.DELETE_IMAGE(encoded));
    },

    // Payments
    async getPayments(params?: { status?: string; search?: string; page?: number; limit?: number }): Promise<any> {
        const apiParams = { ...params };
        if (apiParams.status) apiParams.status = apiParams.status.toUpperCase();
        const response: any = await apiService.get(API_ENDPOINTS.PAYMENTS, apiParams);
        const raw = Array.isArray(response.data) ? response.data : Array.isArray(response) ? response : [];
        return { payments: normalizePayments(raw), pagination: response.pagination };
    },

    async getPaymentById(id: string): Promise<any> {
        const response: any = await apiService.get(API_ENDPOINTS.PAYMENT_BY_ID(id));
        const raw = response.data || response;
        return normalizePayment(raw);
    },

    async getPaymentStats(): Promise<any> {
        const response: any = await apiService.get(API_ENDPOINTS.PAYMENT_STATS);
        return response.data || response;
    },

    async addInstallment(id: string, data: { amount: number; paymentMethod: string; notes?: string }): Promise<any> {
        const response: any = await apiService.post(API_ENDPOINTS.PAYMENT_ADD_INSTALLMENT(id), data);
        return response.data || response;
    },

    async settlePayment(id: string, data: { paymentMethod?: string; notes?: string }): Promise<any> {
        const response: any = await apiService.post(API_ENDPOINTS.PAYMENT_SETTLE(id), data);
        return response.data || response;
    },

    async deletePayment(id: string): Promise<void> {
        await apiService.delete(API_ENDPOINTS.PAYMENT_BY_ID(id));
    },

    // Report preferences
    async sendManualReport(data: {
        email: string;
        period: string;
        startDate?: string;
        endDate?: string;
        indicators: string[];
        agencyIds?: string[];
    }): Promise<string> {
        const response: any = await apiService.post(API_ENDPOINTS.REPORT_SEND, data);
        return (typeof response === "string" ? response : response?.message) || "Rapport envoyé.";
    },

    async getReportPreferences(): Promise<any[]> {
        const response: any = await apiService.get(API_ENDPOINTS.REPORT_PREFERENCES);
        return Array.isArray(response) ? response : (response.data || []);
    },

    async getReportIndicators(): Promise<string[]> {
        const response: any = await apiService.get(API_ENDPOINTS.REPORT_PREFERENCES_INDICATORS);
        return Array.isArray(response) ? response : (response.data || []);
    },

    async upsertReportPreference(frequency: string, data: {
        email?: string | null;
        sendHour?: number;
        indicators?: string[];
        agencyIds?: string[];
        isActive?: boolean;
    }): Promise<any> {
        const response: any = await apiService.put(API_ENDPOINTS.REPORT_PREFERENCE_BY_FREQ(frequency), data);
        return response.data || response;
    },

    async deleteReportPreference(frequency: string): Promise<void> {
        await apiService.delete(API_ENDPOINTS.REPORT_PREFERENCE_BY_FREQ(frequency));
    },

    async sendTestReport(frequency: string): Promise<string> {
        const response: any = await apiService.post(API_ENDPOINTS.REPORT_PREFERENCE_TEST(frequency), {});
        return (typeof response === "string" ? response : response?.message) || "Email de test envoyé.";
    },

    async getReportGlobalSettings(): Promise<{ enabledIndicators: string[]; allowedHours: number[] }> {
        const response: any = await apiService.get(API_ENDPOINTS.REPORT_PREFERENCES_GLOBAL_SETTINGS);
        return response.data || response;
    },

    async updateReportGlobalSettings(data: { enabledIndicators: string[]; allowedHours: number[] }): Promise<any> {
        const response: any = await apiService.put(API_ENDPOINTS.REPORT_PREFERENCES_GLOBAL_SETTINGS, data);
        return response.data || response;
    },

    // Likes (local storage)
    getLikes(): string[] {
        if (typeof window === "undefined") return []
        try {
            const data = localStorage.getItem("stockwise_likes")
            return data ? JSON.parse(data) : []
        } catch {
            return []
        }
    },

    toggleLocalLike(productId: string) {
        const likes = this.getLikes()
        const newLikes = likes.includes(productId) ? likes.filter((id) => id !== productId) : [...likes, productId]
        if (typeof window !== "undefined") {
            localStorage.setItem("stockwise_likes", JSON.stringify(newLikes))
        }
    },
};
